/**
 * /diagnostic — diagnostic assessment entry (wizard).
 *
 * Accepts optional `sessionId`, `pillar`, and `questionId` search params.
 * See HOME_PAGE_REDESIGN.md §6 Phase 2 for the deep-link contract.
 */

import { createFileRoute } from '@tanstack/react-router';
import { DiagnosticWizard } from '@/components/diagnostic/DiagnosticWizard';
import { DiagnosticAccessGate } from '@/components/diagnostic/DiagnosticAccessGate';
import { DiagnosticOpeningCurtain } from '@/components/diagnostic/DiagnosticOpeningCurtain';
import { AdminRoute } from '@/components/guards/AdminRoute';
import { isPillarCode, type PillarCode } from '@/lib/diagnostic/pillar-order';

type DiagnosticSearch = {
  sessionId?: string | null;
  pillar?: PillarCode | null;
  questionId?: string | null;
};

export const Route = createFileRoute('/diagnostic/')({
  component: DiagnosticIndex,
  validateSearch: (search: Record<string, unknown>): DiagnosticSearch => {
    // Return `undefined` (not `null`) for missing/invalid params — TanStack
    // serialises `null` to the URL as the string "null" which then reads
    // back as truthy and trips the wizard's deep-link path. `undefined`
    // keys are omitted from the URL entirely.
    const asClean = (v: unknown): string | undefined => {
      if (typeof v !== 'string') return undefined;
      const t = v.trim();
      return t === '' || t === 'null' || t === 'undefined' ? undefined : t;
    };
    const pillar = isPillarCode(search.pillar) ? search.pillar : undefined;
    return {
      sessionId: asClean(search.sessionId),
      pillar,
      questionId: asClean(search.questionId),
    };
  },
});

function DiagnosticIndex() {
  const { sessionId, pillar, questionId } = Route.useSearch();

  return (
    // One curtain from the first check to the first question: the role, access, session,
    // questions and saved answers all resolve behind it.
    <AdminRoute fallback={<DiagnosticOpeningCurtain />}>
      <DiagnosticAccessGate fallback={<DiagnosticOpeningCurtain />}>
        <div className="container mx-auto px-4">
          <DiagnosticWizard
            sessionId={sessionId}
            initialPillar={pillar}
            initialQuestionId={questionId}
          />
        </div>
      </DiagnosticAccessGate>
    </AdminRoute>
  );
}
